import { useUserStore } from '@/entities/user/@x/study';
import { auth, showMessageError, GETTEXT_ALL_STUDIES } from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import React, { useEffect, useMemo } from 'react';
import { t } from 'ttag';
import fetchStudyDetailsDrawer from '../../api/services/fetchStudyDetailsDrawer';
import { NOT_ASSIGNED_MESSAGE } from '../../lib/notAssignedMessage';
import { formatDataStudy } from '../../lib/studyInfoDrawer';
import type {
  StudyInfo,
  StudyInfoDrawerProps,
  StudyInfoDrawerState,
} from './studyInfoDrawer.types';
import { STUDY_STATUS } from '../../lib/studyConstants';

/**
 * Supplies the study info drawer with its formatted study details, access block and scroll state.
 * Fetches the study whenever the drawer opens with a study id, derives whether a clinic physician
 * outside the all-studies scope must be blocked, scrolls the body to the top on reopen, and
 * exposes a scroll handler.
 *
 * @param props - Drawer props carrying visible, handleClickCloseButton, fetchStudyId, isOngoing and isFlaggedStudiesTab.
 * @returns An object carrying the drawer state, the physician view block flag and the scroll handler.
 */
export const useStudyInfoDrawer = ({
  visible,
  handleClickCloseButton,
  fetchStudyId,
  isOngoing,
  isFlaggedStudiesTab,
}: StudyInfoDrawerProps) => {
  /**
   * Current see-patients scope selected by the signed-in user, used to decide whether the study drawer may be viewed. Read from the user store with a selector so the drawer re-renders when the selection changes.
   *
   * @returns Selected see-patients option held in the user store.
   */
  const selectedTypeSeePatients = useUserStore((s) => s.selectedTypeSeePatients);

  const [state, setState] = useMergeState<StudyInfoDrawerState>({
    data: [],
    study: null,
    interprettingPhysicianId: null,
    isScroll: false,
    isAbortedStudy: false,
    isLoading: false,
    isNotAssigned: false,
  });

  /**
   * Whether the signed-in clinic physician must be blocked from viewing the study currently loaded in the drawer. Memoized over the loaded data, the study's interpreting physician id and the selected see-patients scope: it stays false while no data is loaded or the scope is all studies, and is otherwise true when the user is a clinic physician who is not that study's interpreting physician.
   *
   * @returns True when the study must be hidden from the current clinic physician.
   */
  const blockInterprettingPhysicianViewStudy = useMemo(() => {
    if (_.isEmpty(state.data)) {
      return false;
    }
    if (selectedTypeSeePatients.value === GETTEXT_ALL_STUDIES) {
      return false;
    }
    return auth.isClinicPhysician() && auth.userId() !== state.interprettingPhysicianId;
  }, [state.data, state.interprettingPhysicianId, selectedTypeSeePatients]);

  /**
   * Loads the study details rendered in the study info drawer. Fetches the study, formats it with the ongoing and flagged-tab context and records aborted status plus the interpreting physician id; on error it flags not-assigned from the message, closes the drawer with a study-was-deleted notice when the study is missing and otherwise surfaces the error.
   *
   * @param id - Identifier of the study to load.
   * @returns Resolves after the study data or the error state is stored.
   */
  const handleFetchDataStudy = async (id: string) => {
    try {
      setState({ isLoading: true });
      const study = (await fetchStudyDetailsDrawer(id)) as StudyInfo | null;
      if (!study) {
        setState({ isLoading: false });
        showMessageError({ content: t`Study was deleted` });
        handleClickCloseButton();
        return;
      }
      const data = formatDataStudy(study, {
        isOngoing: isOngoing ?? false,
        isFlaggedStudiesTab: isFlaggedStudiesTab ?? false,
      });
      setState({
        data,
        study,
        isAbortedStudy: study?.status === STUDY_STATUS.ABORTED,
        interprettingPhysicianId: study?.info?.interprettingPhysician?.id,
        isLoading: false,
        isNotAssigned: false,
      });
    } catch (error: any) {
      console.error('Failed to fetch study: ', error);
      setState({
        isLoading: false,
        isNotAssigned: error.message.includes(NOT_ASSIGNED_MESSAGE),
      });

      if (error.message === 'Error: Study not found') {
        showMessageError({ content: t`Study was deleted` });
        handleClickCloseButton();
        return;
      }

      showMessageError({ content: error.message });
    }
  };

  /**
   * Tracks whether the study info drawer body is scrolled so its header can show a divider. Flips the isScroll flag from the scrollTop of the event's current target.
   *
   * @param e - Scroll event from the drawer body.
   * @returns Nothing.
   */
  const handleScroll = (e: React.UIEvent<HTMLElement>) => {
    const { scrollTop } = e.currentTarget;
    if (scrollTop === 0 && state.isScroll) {
      setState({ isScroll: false });
    } else if (scrollTop !== 0 && !state.isScroll) {
      setState({ isScroll: true });
    }
  };

  useEffect(() => {
    if (fetchStudyId && visible) {
      handleFetchDataStudy(fetchStudyId);
    }
  }, [visible]);

  useUpdateEffect(() => {
    if (visible) {
      const myDiv = document.getElementById('drawer-body-container');
      if (myDiv) {
        myDiv.scrollTo({ top: 0 });
      }
    }
  }, [visible]);

  return { state, blockInterprettingPhysicianViewStudy, handleScroll };
};
